import { useState } from 'react';
import { useWorkspaces } from '@/store/WorkspaceContext';
import { useUi } from '@/store/UiContext';
import { Modal } from '@/components/ui/Modal';
import { RoleBadge } from '@/components/ui/RoleBadge';
import { useToast } from '@/components/ui/Toast';
import './WorkspaceManager.css';

/**
 * Rename, delete, and leave workspaces.
 *
 * Separate from MembersDialog, which is about *who* is in the workspace you have
 * open. This is about the workspaces themselves, so it has to list all of them —
 * including ones that are not currently active.
 *
 * Delete requires typing the workspace name. It is not covered by the shared
 * `useConfirm` dialog on purpose: that dialog offers "don't ask again", which
 * must never be available for an unrecoverable, whole-workspace delete.
 */

type PendingAction = { id: string; kind: 'rename' | 'delete' | 'leave' } | null;

export function WorkspaceManager() {
  const ws = useWorkspaces();
  const ui = useUi();
  const notify = useToast();

  const [pending, setPending] = useState<PendingAction>(null);
  const [draftName, setDraftName] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);

  const close = () => ui.setWorkspaceManagerOpen(false);

  const begin = (id: string, kind: 'rename' | 'delete' | 'leave', currentName: string) => {
    setPending({ id, kind });
    setDraftName(currentName);
    setConfirmText('');
  };

  const cancel = () => {
    setPending(null);
    setConfirmText('');
  };

  /** Shared tail for every mutation: report, reset, and never leave `busy` stuck. */
  const run = (label: string, work: Promise<void>) => {
    setBusy(true);
    void work
      .then(() => {
        notify(label);
        cancel();
      })
      .catch((err) => notify(err instanceof Error ? err.message : String(err), 'error'))
      .finally(() => setBusy(false));
  };

  const submitRename = (id: string, previous: string) => {
    const name = draftName.trim();
    if (!name || name === previous) return cancel();
    run(`Renamed to “${name}”`, ws.rename(id, name));
  };

  return (
    <Modal
      open
      size="lg"
      onClose={close}
      title="Manage workspaces"
      subtitle="Rename, leave, or delete a workspace"
      footer={
        <>
          <button className="ghost" onClick={close}>
            Done
          </button>
          <span className="spacer" />
        </>
      }
    >
      <ul className="wm-list">
        {ws.workspaces.map((w) => {
          const isOwner = w.role === 'owner';
          const isActive = w.id === ws.active?.id;
          const isLast = ws.workspaces.length === 1;
          const editing = pending?.id === w.id ? pending.kind : null;

          return (
            <li className={`wm-item${isActive ? ' on' : ''}`} key={w.id}>
              <div className="wm-row">
                <div className="wm-identity">
                  {editing === 'rename' ? (
                    <input
                      className="wm-input"
                      type="text"
                      autoFocus
                      maxLength={120}
                      value={draftName}
                      onChange={(e) => setDraftName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') submitRename(w.id, w.name);
                        if (e.key === 'Escape') cancel();
                      }}
                    />
                  ) : (
                    <span className="wm-name">{w.name}</span>
                  )}

                  <div className="wm-sub">
                    <RoleBadge role={w.role} />
                    <span className="wm-members">
                      {w.memberCount} member{w.memberCount === 1 ? '' : 's'}
                    </span>
                    {isActive ? <span className="wm-current">open now</span> : null}
                  </div>
                </div>

                <div className="wm-actions">
                  {editing === 'rename' ? (
                    <>
                      <button
                        className="tiny primary"
                        disabled={busy}
                        onClick={() => submitRename(w.id, w.name)}
                      >
                        Save
                      </button>
                      <button className="tiny ghost" disabled={busy} onClick={cancel}>
                        Cancel
                      </button>
                    </>
                  ) : editing ? null : (
                    <>
                      {!isActive ? (
                        <button
                          className="tiny ghost"
                          onClick={() => {
                            ws.select(w.id);
                            close();
                          }}
                        >
                          Open
                        </button>
                      ) : null}

                      {isOwner ? (
                        <>
                          <button
                            className="tiny ghost"
                            onClick={() => begin(w.id, 'rename', w.name)}
                          >
                            Rename
                          </button>
                          <button
                            className="tiny danger"
                            disabled={isLast}
                            title={
                              isLast
                                ? 'This is your only workspace. Create another one first.'
                                : 'Delete this workspace and everything in it'
                            }
                            onClick={() => begin(w.id, 'delete', w.name)}
                          >
                            Delete
                          </button>
                        </>
                      ) : (
                        <button
                          className="tiny danger"
                          disabled={isLast}
                          title={
                            isLast
                              ? 'This is your only workspace.'
                              : 'Remove yourself from this workspace'
                          }
                          onClick={() => begin(w.id, 'leave', w.name)}
                        >
                          Leave
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/*
                Type-to-confirm. The board, its snapshots, the member list and any
                pending invitations all go, and nothing here can bring them back —
                so the gesture has to be more deliberate than clicking twice.
              */}
              {editing === 'delete' ? (
                <div className="wm-danger">
                  <p>
                    Deleting <strong>{w.name}</strong> removes its board, every snapshot, the
                    member list, and all pending invitations for{' '}
                    <strong>
                      {w.memberCount} member{w.memberCount === 1 ? '' : 's'}
                    </strong>
                    . This cannot be undone.
                  </p>
                  <label className="wm-confirm-label">
                    Type <code>{w.name}</code> to confirm
                  </label>
                  <div className="wm-confirm-row">
                    <input
                      className="wm-input"
                      type="text"
                      autoFocus
                      value={confirmText}
                      onChange={(e) => setConfirmText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') cancel();
                      }}
                    />
                    <button
                      className="tiny danger"
                      disabled={busy || confirmText.trim() !== w.name}
                      onClick={() => run(`Deleted “${w.name}”`, ws.remove(w.id))}
                    >
                      {busy ? 'Deleting…' : 'Delete permanently'}
                    </button>
                    <button className="tiny ghost" disabled={busy} onClick={cancel}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : null}

              {editing === 'leave' ? (
                <div className="wm-danger">
                  <p>
                    You will lose access to <strong>{w.name}</strong>. An owner can invite you
                    back.
                  </p>
                  <div className="wm-confirm-row">
                    <button
                      className="tiny danger"
                      disabled={busy}
                      onClick={() => run(`Left “${w.name}”`, ws.leave(w.id))}
                    >
                      {busy ? 'Leaving…' : 'Leave workspace'}
                    </button>
                    <button className="tiny ghost" disabled={busy} onClick={cancel}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>

      {ws.workspaces.length === 0 ? <p className="wm-empty">No workspaces yet.</p> : null}

      <p className="settings-hint wm-foot">
        Only an owner can rename or delete a workspace. To hand a workspace over instead of
        deleting it, transfer ownership from Members and sharing.
      </p>
    </Modal>
  );
}
